import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessProfile, updateBusinessProfile, getBusinessEmployees, updateEmployeeStatus, generateRegistrationCode } from '../../services/businessService';
import type { BusinessData, EmployeeData } from '../../services/businessService';
import { uploadBusinessLogo, deleteBusinessLogo, extractFileNameFromUrl } from '../../services/fileUploadService';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FaEdit, FaSave, FaTimes, FaBuilding, FaEnvelope, FaPhone, FaUsers, FaMapMarkerAlt, FaPlus, FaTrash, FaBriefcase, FaCalculator, FaCamera, FaCopy, FaCheck, FaExternalLinkAlt, FaUserCheck, FaUserTimes, FaSync } from 'react-icons/fa';

interface Service {
  id: string;
  name: string;
  description: string;
  price?: string;
  priceType?: 'hourly' | 'daily' | 'fixed';
}

interface Tax {
  id: string;
  name: string;
  percentage: number;
  description?: string;
  isActive: boolean;
}

const MyBusiness: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [businessData, setBusinessData] = useState<BusinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Partial<BusinessData>>({});
  const [services, setServices] = useState<Service[]>([]);
  const [isAddingService, setIsAddingService] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'information' | 'services' | 'taxes' | 'roster'>('information');
  
  // Estados para Roster
  const [employees, setEmployees] = useState<EmployeeData[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [registrationCode, setRegistrationCode] = useState<string>('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [newService, setNewService] = useState<Omit<Service, 'id'>>({
    name: '',
    description: '',
    price: '',
    priceType: 'fixed'
  });
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [isAddingTax, setIsAddingTax] = useState(false);
  const [editingTaxId, setEditingTaxId] = useState<string | null>(null);
  const [newTax, setNewTax] = useState<Omit<Tax, 'id'>>({
    name: '',
    percentage: 0,
    description: '',
    isActive: true
  });

  // Estados para manejo de logo
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        if (currentUser?.uid) {
          const data = await getBusinessProfile(currentUser.uid);
          setBusinessData(data);
          setEditedData(data);
          setRegistrationCode(data.employeeRegistrationCode || '');
          
          // Parsear servicios si existen
          if (data.services) {
            try {
              const parsedServices = JSON.parse(data.services);
              setServices(Array.isArray(parsedServices) ? parsedServices : []);
            } catch {
              // Si no es JSON válido, tratar como string simple
              setServices([{
                id: '1',
                name: data.services,
                description: '',
                price: '',
                priceType: 'fixed'
              }]);
            }
          }
          
          // Parsear impuestos si existen
          if (data.taxes) {
            try {
              const parsedTaxes = JSON.parse(data.taxes);
              setTaxes(Array.isArray(parsedTaxes) ? parsedTaxes : []);
            } catch {
              setTaxes([]);
            }
          }
        }
      } catch (err) {
        setError(t('errors.fetchBusinessData'));
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [currentUser, t]);

  // Cargar empleados cuando se activa el tab de roster
  useEffect(() => {
    const fetchEmployees = async () => {
      if (activeTab === 'roster' && businessData?.id) {
        setLoadingEmployees(true);
        try {
          const employeesData = await getBusinessEmployees(businessData.id);
          setEmployees(employeesData);
        } catch (err) {
          console.error('Error loading employees:', err);
        } finally {
          setLoadingEmployees(false);
        }
      }
    };

    fetchEmployees();
  }, [activeTab, businessData?.id]);

  // Limpiar mensaje de éxito automáticamente
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData(businessData || {});
  };

  const handleSave = async () => {
    try {
      if (!currentUser?.uid) {
        throw new Error('No user logged in');
      }
      setIsSaving(true);
      await updateBusinessProfile(currentUser.uid, editedData);
      setIsEditing(false);
      setBusinessData(editedData as BusinessData);
      setSuccessMessage(t('common.updateSuccess'));
    } catch (err) {
      setError(t('errors.updateBusinessData'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddService = () => {
    setIsAddingService(true);
    setNewService({ name: '', description: '', price: '', priceType: 'fixed' });
  };

  const handleCancelAddService = () => {
    setIsAddingService(false);
    setNewService({ name: '', description: '', price: '', priceType: 'fixed' });
  };

  const handleSaveService = () => {
    if (newService.name.trim()) {
      const serviceToAdd: Service = {
        id: Date.now().toString(),
        ...newService
      };
      setServices(prev => [...prev, serviceToAdd]);
      setIsAddingService(false);
      setNewService({ name: '', description: '', price: '', priceType: 'fixed' });
      
      // Actualizar el campo services en editedData
      const updatedServices = [...services, serviceToAdd];
      setEditedData(prev => ({
        ...prev,
        services: JSON.stringify(updatedServices)
      }));
      setSuccessMessage(t('common.addSuccess'));
    }
  };

  const handleEditService = (serviceId: string) => {
    setEditingServiceId(serviceId);
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setNewService({
        name: service.name,
        description: service.description,
        price: service.price || '',
        priceType: service.priceType || 'fixed'
      });
    }
  };

  const handleUpdateService = () => {
    if (editingServiceId && newService.name.trim()) {
      setServices(prev => prev.map(service => 
        service.id === editingServiceId 
          ? { ...service, ...newService }
          : service
      ));
      setEditingServiceId(null);
      setNewService({ name: '', description: '', price: '', priceType: 'fixed' });
      
      // Actualizar el campo services en editedData
      const updatedServices = services.map(service => 
        service.id === editingServiceId 
          ? { ...service, ...newService }
          : service
      );
      setEditedData(prev => ({
        ...prev,
        services: JSON.stringify(updatedServices)
      }));
      setSuccessMessage(t('common.updateSuccess'));
    }
  };

  const handleDeleteService = (serviceId: string) => {
    const updatedServices = services.filter(service => service.id !== serviceId);
    setServices(updatedServices);
    
    // Actualizar el campo services en editedData
    setEditedData(prev => ({
      ...prev,
      services: JSON.stringify(updatedServices)
    }));
    setSuccessMessage(t('common.deleteSuccess'));
  };

  const handleServiceInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewService(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Funciones para manejar impuestos
  const handleAddTax = () => {
    setIsAddingTax(true);
    setNewTax({ name: '', percentage: 0, description: '', isActive: true });
  };

  const handleCancelAddTax = () => {
    setIsAddingTax(false);
    setNewTax({ name: '', percentage: 0, description: '', isActive: true });
  };

  const handleSaveTax = () => {
    if (newTax.name.trim() && newTax.percentage > 0) {
      const taxToAdd: Tax = {
        id: Date.now().toString(),
        ...newTax
      };
      setTaxes(prev => [...prev, taxToAdd]);
      setIsAddingTax(false);
      setNewTax({ name: '', percentage: 0, description: '', isActive: true });
      
      // Actualizar el campo taxes en editedData
      const updatedTaxes = [...taxes, taxToAdd];
      setEditedData(prev => ({
        ...prev,
        taxes: JSON.stringify(updatedTaxes)
      }));
      setSuccessMessage(t('common.addSuccess'));
    }
  };

  const handleEditTax = (taxId: string) => {
    setEditingTaxId(taxId);
    const tax = taxes.find(t => t.id === taxId);
    if (tax) {
      setNewTax({
        name: tax.name,
        percentage: tax.percentage,
        description: tax.description || '',
        isActive: tax.isActive
      });
    }
  };

  const handleUpdateTax = () => {
    if (editingTaxId && newTax.name.trim() && newTax.percentage > 0) {
      setTaxes(prev => prev.map(tax => 
        tax.id === editingTaxId 
          ? { ...tax, ...newTax }
          : tax
      ));
      setEditingTaxId(null);
      setNewTax({ name: '', percentage: 0, description: '', isActive: true });
      
      // Actualizar el campo taxes en editedData
      const updatedTaxes = taxes.map(tax => 
        tax.id === editingTaxId 
          ? { ...tax, ...newTax }
          : tax
      );
      setEditedData(prev => ({
        ...prev,
        taxes: JSON.stringify(updatedTaxes)
      }));
      setSuccessMessage(t('common.updateSuccess'));
    }
  };

  const handleDeleteTax = (taxId: string) => {
    const updatedTaxes = taxes.filter(tax => tax.id !== taxId);
    setTaxes(updatedTaxes);
    
    // Actualizar el campo taxes en editedData
    setEditedData(prev => ({
      ...prev,
      taxes: JSON.stringify(updatedTaxes)
    }));
    setSuccessMessage(t('common.deleteSuccess'));
  };

  const handleTaxInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setNewTax(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              value
    }));
  };

  // Funciones para manejo de logo
  const handleLogoClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser?.uid) return;

    setIsUploadingLogo(true);
    setLogoError(null);

    try {
      // Obtener el businessId del usuario
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      
      console.log('Debug - User data:', userData);
      console.log('Debug - Current user UID:', currentUser.uid);
      console.log('Debug - Business ID from user:', userData?.businessId);
      
      if (!userData?.businessId) {
        throw new Error('No business associated with this user');
      }

      // Subir nueva imagen
      const uploadResult = await uploadBusinessLogo(file, userData.businessId);
      
      if (uploadResult.success && uploadResult.url) {
        // Eliminar logo anterior si existe
        if (businessData?.logo) {
          const oldFileName = extractFileNameFromUrl(businessData.logo);
          if (oldFileName) {
            await deleteBusinessLogo(userData.businessId, oldFileName);
          }
        }

        // Actualizar el estado local
        const updatedData = { ...editedData, logo: uploadResult.url };
        setEditedData(updatedData);
        setBusinessData(prev => prev ? { ...prev, logo: uploadResult.url } : null);
        
        setSuccessMessage(t('business.logoUpdated'));
      } else {
        setLogoError(uploadResult.error || t('business.logoUploadError'));
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setLogoError(t('business.logoUploadError'));
    } finally {
      setIsUploadingLogo(false);
      // Limpiar el input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!businessData?.logo || !currentUser?.uid) return;

    setIsUploadingLogo(true);
    setLogoError(null);

    try {
      // Obtener el businessId del usuario
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      
      console.log('Debug - Remove logo - User data:', userData);
      console.log('Debug - Remove logo - Business ID from user:', userData?.businessId);
      
      if (!userData?.businessId) {
        throw new Error('No business associated with this user');
      }

      const fileName = extractFileNameFromUrl(businessData.logo);
      if (fileName) {
        const deleteResult = await deleteBusinessLogo(userData.businessId, fileName);
        if (deleteResult.success) {
          const updatedData = { ...editedData, logo: undefined };
          setEditedData(updatedData);
          setBusinessData(prev => prev ? { ...prev, logo: undefined } : null);
          setSuccessMessage(t('business.logoRemoved'));
        } else {
          setLogoError(deleteResult.error || t('business.logoRemoveError'));
        }
      }
    } catch (error) {
      console.error('Error removing logo:', error);
      setLogoError(t('business.logoRemoveError'));
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Funciones para Roster
  const handleGenerateCode = async () => {
    if (!currentUser?.uid) return;
    
    setGeneratingCode(true);
    try {
      const newCode = generateRegistrationCode();
      await updateBusinessProfile(currentUser.uid, { employeeRegistrationCode: newCode });
      setRegistrationCode(newCode);
      setBusinessData(prev => prev ? { ...prev, employeeRegistrationCode: newCode } : null);
      setSuccessMessage(t('roster.codeGenerated'));
    } catch (error) {
      console.error('Error generating code:', error);
      setError(t('roster.errorGeneratingCode'));
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyCode = () => {
    if (registrationCode) {
      navigator.clipboard.writeText(registrationCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleCopyLink = () => {
    if (businessData?.id) {
      const link = `${window.location.origin}/employee-registration/${businessData.id}`;
      navigator.clipboard.writeText(link);
      setSuccessMessage(t('roster.linkCopied'));
    }
  };

  const handleApproveEmployee = async (userId: string) => {
    try {
      await updateEmployeeStatus(userId, 'active');
      setEmployees(prev => prev.map(emp => 
        emp.id === userId ? { ...emp, status: 'active' } : emp
      ));
      setSuccessMessage(t('roster.employeeApproved'));
    } catch (error) {
      console.error('Error approving employee:', error);
      setError(t('roster.errorApprovingEmployee'));
    }
  };

  const handleRejectEmployee = async (userId: string) => {
    try {
      await updateEmployeeStatus(userId, 'inactive');
      setEmployees(prev => prev.map(emp => 
        emp.id === userId ? { ...emp, status: 'inactive' } : emp
      ));
      setSuccessMessage(t('roster.employeeRejected'));
    } catch (error) {
      console.error('Error rejecting employee:', error);
      setError(t('roster.errorRejectingEmployee'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8 font-sans">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8 font-sans">
        <div className="flex items-center justify-center min-h-screen">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <p className="text-red-700 text-center">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8 font-sans">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-8 mb-12 p-10 bg-white rounded-3xl shadow-2xl border border-white/40 animate-fadeIn">
          {/* Logo Section */}
          <div className="relative">
            <div 
              className={`w-36 h-36 rounded-2xl overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center shadow-xl border border-white/20 relative group ${
                isEditing ? 'cursor-pointer' : 'cursor-default'
              }`}
              onClick={handleLogoClick}
            >
              <FaBuilding className="absolute top-3 left-3 text-red-200 text-3xl z-10" />
              {isUploadingLogo ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : businessData?.logo ? (
                <img 
                  src={businessData.logo} 
                  alt={businessData.companyName} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-5xl font-bold text-gray-500 bg-gradient-to-br from-gray-200 to-gray-300">
                  {businessData?.companyName?.charAt(0) || 'B'}
                </div>
              )}
              {isEditing && !isUploadingLogo && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <FaCamera className="text-white text-2xl" />
                </div>
              )}
            </div>
            
            {/* Input file oculto */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
            />
            
            {/* Botón para eliminar logo */}
            {isEditing && businessData?.logo && !isUploadingLogo && (
              <button
                onClick={handleRemoveLogo}
                className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
                title={t('business.removeLogo')}
              >
                <FaTrash className="text-sm" />
              </button>
            )}
          </div>

          {/* Business Info */}
          <div className="flex-1">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight drop-shadow-lg">
              <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
                {businessData?.companyName}
              </span>
            </h1>
            <p className="text-gray-600 text-xl font-medium mb-4 flex items-center gap-2">
              <FaMapMarkerAlt className="text-red-400" />
              {businessData?.businessIdType}: {businessData?.businessId}
            </p>
            <span className="inline-block px-6 py-3 rounded-full bg-gradient-to-r from-red-600 to-red-500 text-white text-base font-semibold shadow-lg">
              {t(`business.statuses.${businessData?.status?.toLowerCase() || 'pending'}`)}
            </span>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl animate-fadeIn">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <p className="text-green-700 font-medium">{successMessage}</p>
              <button
                onClick={() => setSuccessMessage(null)}
                className="ml-auto text-green-500 hover:text-green-700"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}

        {/* Logo Error Message */}
        {logoError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl animate-fadeIn">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <p className="text-red-700 font-medium">{logoError}</p>
              <button
                onClick={() => setLogoError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="bg-white rounded-3xl p-2 shadow-2xl border border-white/40 animate-fadeIn mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button
              onClick={() => setActiveTab('information')}
              className={`px-6 py-4 rounded-2xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'information'
                  ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FaBuilding />
              {t('business.information')}
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`px-6 py-4 rounded-2xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'services'
                  ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FaBriefcase />
              {t('business.services')}
            </button>
            <button
              onClick={() => setActiveTab('taxes')}
              className={`px-6 py-4 rounded-2xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'taxes'
                  ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FaCalculator />
              {t('business.taxes')}
            </button>
            <button
              onClick={() => setActiveTab('roster')}
              className={`px-6 py-4 rounded-2xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'roster'
                  ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FaUsers />
              {t('roster.title')}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'information' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 animate-fadeIn">
            {/* Contact Info Section */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40">
            <h2 className="text-2xl font-bold text-red-600 mb-6 flex items-center gap-2"><FaMapMarkerAlt className="text-red-400" />{t('business.contactInfo')}</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-red-400" />{t('business.address')}
                </label>
                {isEditing ? (
                  <textarea
                    name="address"
                    value={editedData.address || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                  />
                ) : (
                  <p className="text-gray-900">{businessData?.address || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FaPhone className="text-red-400" />{t('business.phone')}
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={editedData.phone || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                  />
                ) : (
                  <p className="text-gray-900">{businessData?.phone || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FaEnvelope className="text-red-400" />{t('business.email')}
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="businessEmail"
                    value={editedData.businessEmail || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                  />
                ) : (
                  <p className="text-gray-900">{businessData?.businessEmail || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FaUsers className="text-red-400" />{t('business.employeeCount')}
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    name="employeeCount"
                    value={editedData.employeeCount || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                  />
                ) : (
                  <p className="text-gray-900">{businessData?.employeeCount || '-'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Info Section */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40 animate-fadeIn">
            <h2 className="text-2xl font-bold text-red-600 mb-6 flex items-center gap-2"><FaEdit className="text-red-400" />{t('business.additionalInfo')}</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FaEdit className="text-red-400" />{t('business.description')}
                </label>
                {isEditing ? (
                  <textarea
                    name="description"
                    value={editedData.description || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base min-h-24 resize-y transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                  />
                ) : (
                  <p className="text-gray-900">{businessData?.description || '-'}</p>
                )}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40 animate-fadeIn mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <FaBriefcase className="text-red-400" />
              {t('business.services')}
            </h2>
            {isEditing && (
              <button
                onClick={handleAddService}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:transform hover:-translate-y-1 shadow-lg"
              >
                <FaPlus />
                {t('common.add')}
              </button>
            )}
          </div>

          {/* Add/Edit Service Form */}
          {(isAddingService || editingServiceId) && (
            <div className="bg-gray-50 rounded-2xl p-6 mb-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {editingServiceId ? t('common.edit') : t('common.add')} {t('business.services').toLowerCase()}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('business.serviceName')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newService.name}
                    onChange={handleServiceInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600"
                    placeholder={t('business.serviceNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('business.servicePrice')}
                  </label>
                  <input
                    type="text"
                    name="price"
                    value={newService.price}
                    onChange={handleServiceInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600"
                    placeholder={t('business.servicePricePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('business.servicePriceType')}
                  </label>
                  <select
                    name="priceType"
                    value={newService.priceType}
                    onChange={handleServiceInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600"
                  >
                    <option value="fixed">{t('business.priceTypes.fixed')}</option>
                    <option value="hourly">{t('business.priceTypes.hourly')}</option>
                    <option value="daily">{t('business.priceTypes.daily')}</option>
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('business.serviceDescription')}
                </label>
                <textarea
                  name="description"
                  value={newService.description}
                  onChange={handleServiceInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base min-h-20 resize-y transition-all duration-300 focus:outline-none focus:border-red-600"
                  placeholder={t('business.serviceDescriptionPlaceholder')}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={editingServiceId ? handleUpdateService : handleSaveService}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:transform hover:-translate-y-1 shadow-lg"
                >
                  <FaSave />
                  {editingServiceId ? t('common.save') : t('common.add')}
                </button>
                <button
                  onClick={handleCancelAddService}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:transform hover:-translate-y-1 shadow-lg"
                >
                  <FaTimes />
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Services List */}
          <div className="space-y-4">
            {services.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FaBriefcase className="text-4xl mx-auto mb-4 text-gray-300" />
                <p className="text-lg">{t('business.noServices')}</p>
                {isEditing && (
                  <p className="text-sm mt-2">{t('business.addFirstService')}</p>
                )}
              </div>
            ) : (
              services.map((service) => (
                <div
                  key={service.id}
                  className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">{service.name}</h3>
                        {service.price && (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            {service.price} {service.priceType && t(`business.priceTypes.${service.priceType}`)}
                          </span>
                        )}
                      </div>
                      {service.description && (
                        <p className="text-gray-600 text-sm">{service.description}</p>
                      )}
                    </div>
                    {isEditing && (
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEditService(service.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                          title={t('common.edit')}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          title={t('common.delete')}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          </div>
        )}

        {/* Taxes Tab */}
        {activeTab === 'taxes' && (
          <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40 animate-fadeIn mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <FaCalculator className="text-red-400" />
              {t('business.taxes')}
            </h2>
            {isEditing && (
              <button
                onClick={handleAddTax}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:transform hover:-translate-y-1 shadow-lg"
              >
                <FaPlus />
                {t('common.add')}
              </button>
            )}
          </div>

          {/* Add/Edit Tax Form */}
          {(isAddingTax || editingTaxId) && (
            <div className="bg-gray-50 rounded-2xl p-6 mb-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {editingTaxId ? t('common.edit') : t('common.add')} {t('business.taxes').toLowerCase()}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('business.taxName')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newTax.name}
                    onChange={handleTaxInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600"
                    placeholder={t('business.taxNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('business.taxPercentage')}
                  </label>
                  <input
                    type="number"
                    name="percentage"
                    value={newTax.percentage}
                    onChange={handleTaxInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600"
                    placeholder={t('business.taxPercentagePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('business.taxDescription')}
                  </label>
                  <textarea
                    name="description"
                    value={newTax.description}
                    onChange={handleTaxInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base min-h-20 resize-y transition-all duration-300 focus:outline-none focus:border-red-600"
                    placeholder={t('business.taxDescriptionPlaceholder')}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={editingTaxId ? handleUpdateTax : handleSaveTax}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:transform hover:-translate-y-1 shadow-lg"
                >
                  <FaSave />
                  {editingTaxId ? t('common.save') : t('common.add')}
                </button>
                <button
                  onClick={handleCancelAddTax}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:transform hover:-translate-y-1 shadow-lg"
                >
                  <FaTimes />
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Taxes List */}
          <div className="space-y-4">
            {taxes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FaCalculator className="text-4xl mx-auto mb-4 text-gray-300" />
                <p className="text-lg">{t('business.noTaxes')}</p>
                {isEditing && (
                  <p className="text-sm mt-2">{t('business.addFirstTax')}</p>
                )}
              </div>
            ) : (
              taxes.map((tax) => (
                <div
                  key={tax.id}
                  className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">{tax.name}</h3>
                        {tax.percentage > 0 && (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            {tax.percentage}%
                          </span>
                        )}
                      </div>
                      {tax.description && (
                        <p className="text-gray-600 text-sm">{tax.description}</p>
                      )}
                    </div>
                    {isEditing && (
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEditTax(tax.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                          title={t('common.edit')}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteTax(tax.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          title={t('common.delete')}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          </div>
        )}

        {/* Roster Tab */}
        {activeTab === 'roster' && (
          <div className="space-y-8 animate-fadeIn">
            {/* Registration Code Section */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40">
              <h2 className="text-2xl font-bold text-red-600 mb-6 flex items-center gap-2">
                <FaUsers className="text-red-400" />
                {t('roster.registrationCode')}
              </h2>
              
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 mb-6 border-2 border-red-200">
                <p className="text-gray-700 mb-4">{t('roster.codeDescription')}</p>
                
                {registrationCode ? (
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1 bg-white rounded-xl p-4 border-2 border-red-300">
                      <p className="text-xs text-gray-500 mb-1">{t('roster.currentCode')}</p>
                      <p className="text-3xl font-bold text-red-600 tracking-widest">{registrationCode}</p>
                    </div>
                    <button
                      onClick={handleCopyCode}
                      className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-300 flex items-center gap-2"
                      title={t('roster.copyCode')}
                    >
                      {copiedCode ? <FaCheck /> : <FaCopy />}
                      {copiedCode ? t('common.copied') : t('common.copy')}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 mb-4">
                    <p>{t('roster.noCodeYet')}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleGenerateCode}
                    disabled={generatingCode}
                    className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg"
                  >
                    {generatingCode ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {t('roster.generating')}
                      </>
                    ) : (
                      <>
                        <FaSync />
                        {registrationCode ? t('roster.regenerateCode') : t('roster.generateCode')}
                      </>
                    )}
                  </button>

                  {registrationCode && businessData?.id && (
                    <button
                      onClick={handleCopyLink}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg"
                    >
                      <FaExternalLinkAlt />
                      {t('roster.copyRegistrationLink')}
                    </button>
                  )}
                </div>
              </div>

              {registrationCode && businessData?.id && (
                <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                  <p className="text-sm text-gray-700 mb-2 font-semibold">{t('roster.registrationLink')}</p>
                  <p className="text-xs text-gray-600 break-all font-mono bg-white p-3 rounded-lg border border-blue-200">
                    {`${window.location.origin}/employee-registration/${businessData.id}`}
                  </p>
                </div>
              )}
            </div>

            {/* Employees List */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-red-600 flex items-center gap-2">
                  <FaUsers className="text-red-400" />
                  {t('roster.employees')}
                </h2>
                <span className="px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                  {employees.length} {employees.length === 1 ? t('roster.employee') : t('roster.employeesPlural')}
                </span>
              </div>

              {loadingEmployees ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t('common.loading')}</p>
                </div>
              ) : employees.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FaUsers className="text-6xl mx-auto mb-4 text-gray-300" />
                  <p className="text-lg mb-2">{t('roster.noEmployees')}</p>
                  <p className="text-sm">{t('roster.shareRegistrationLink')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {employees.map((employee) => (
                    <div
                      key={employee.id}
                      className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                              {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">
                                {employee.firstName} {employee.lastName}
                              </h3>
                              <p className="text-sm text-gray-600">{employee.title}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FaBriefcase className="text-red-400" />
                              <span>{employee.position}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FaEnvelope className="text-red-400" />
                              <span>{employee.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FaPhone className="text-red-400" />
                              <span>{employee.phone}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FaUsers className="text-red-400" />
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                employee.status === 'active' ? 'bg-green-100 text-green-700' :
                                employee.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {t(`roster.status.${employee.status}`)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {employee.status === 'pending' && (
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleApproveEmployee(employee.id)}
                              className="p-3 bg-green-100 hover:bg-green-200 text-green-700 rounded-xl transition-colors duration-200 flex items-center gap-2"
                              title={t('roster.approve')}
                            >
                              <FaUserCheck />
                            </button>
                            <button
                              onClick={() => handleRejectEmployee(employee.id)}
                              className="p-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl transition-colors duration-200 flex items-center gap-2"
                              title={t('roster.reject')}
                            >
                              <FaUserTimes />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 justify-end mt-8">
          {isEditing ? (
            <>
              <button 
                onClick={handleCancel}
                className="px-6 py-3 border-none rounded-xl bg-gray-500 hover:bg-gray-600 text-white text-base font-semibold cursor-pointer transition-all duration-300 flex items-center gap-2 hover:transform hover:-translate-y-1 shadow-lg"
              >
                <FaTimes />
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-3 border-none rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-base font-semibold cursor-pointer transition-all duration-300 flex items-center gap-2 hover:transform hover:-translate-y-1 shadow-lg"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <FaSave />
                    {t('common.save')}
                  </>
                )}
              </button>
            </>
          ) : (
            <button 
              onClick={handleEdit}
              className="px-6 py-3 border-none rounded-xl bg-red-600 hover:bg-red-700 text-white text-base font-semibold cursor-pointer transition-all duration-300 flex items-center gap-2 hover:transform hover:-translate-y-1 shadow-lg"
            >
              <FaEdit />
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyBusiness; 